import {useDiffEntries, useThreads} from '../stores/selectors'
import type {DiffEntry} from '@github-ui/diff-lines'
import {type Thread, CommentStates} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'
import type {PendingComment} from '../page-data/payloads/thread-previews'
import {
  positionedThreadToSubject,
  useThreadPreviewPositioning,
} from '../page-data/loaders/use-thread-preview-positioning'
import {buildDiffSubject} from '../helpers/diff-helpers'
import {useCompositeThreadPositioning} from './use-composite-thread-positioning'

/**
 * Helper function to extract pending comments from threads
 */
function extractPendingCommentsFromThreads(threads: Thread[]): PendingComment[] {
  const comments: PendingComment[] = []

  for (const thread of threads) {
    // Get pending comments from this thread
    const threadPendingComments = (thread.commentsData?.comments ?? []).filter(
      comment => comment.state === CommentStates.Pending,
    )

    for (const currentComment of threadPendingComments) {
      if (!currentComment.databaseId) {
        continue
      }

      comments.push({
        commentID: currentComment.databaseId,
        thread,
      })
    }
  }

  return comments
}

/**
 * Hook for getting pending review thread previews using store-based positioning (new approach)
 * Uses the useCompositeThreadPositioning hook to apply positioning data to threads in the store
 */
export function useGetPendingCommentsFromStore(pendingReviewIds?: number[]): PendingComment[] {
  const threads = useThreads(pendingReviewIds)
  const diffEntries: Record<string, DiffEntry> = useDiffEntries()

  // Use new positioning hook to handle both inside and outside diff threads
  useCompositeThreadPositioning(threads, diffEntries)

  // Extract pending comments from threads with positioning already applied
  const commentsList = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return []
    }

    return extractPendingCommentsFromThreads(threads)
  }, [threads, pendingReviewIds])

  return commentsList
}

export function useGetPendingCommentsFromStoreWithoutDiffEntries(pendingReviewIds?: number[]): PendingComment[] {
  const threads = useThreads(pendingReviewIds)

  useCompositeThreadPositioning(threads)

  // Extract pending comments from threads with positioning already applied
  const commentsList = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return []
    }

    return extractPendingCommentsFromThreads(threads)
  }, [threads, pendingReviewIds])

  return commentsList
}

/**
 * Hook for getting pending review thread previews with positioning data stitched in (legacy approach)
 * Returns PendingComment format with the full Thread object for each pending comment
 */
export function useGetPendingComments(pendingReviewIds?: number[]): PendingComment[] {
  const threads = useThreads(pendingReviewIds)
  const diffEntries = useDiffEntries()

  const {pendingComments, threadsWithoutContext} = useMemo(() => {
    if (!pendingReviewIds || pendingReviewIds.length === 0 || !threads) {
      return {pendingComments: [], threadsWithoutContext: []}
    }

    const threadsWithoutContextInt: number[] = []
    const threadsWithSubject: Thread[] = []

    for (const thread of threads) {
      const {diffSubject, hasContext} = buildDiffSubject(thread, diffEntries)

      if (!hasContext) {
        threadsWithoutContextInt.push(Number(thread.id))
      }

      // Create a thread object with the built subject data and path
      const threadWithSubject: Thread = {
        ...thread,
        path: thread.positioning?.path || '',
        subject: diffSubject || thread.subject,
      }

      threadsWithSubject.push(threadWithSubject)
    }

    const comments = extractPendingCommentsFromThreads(threadsWithSubject)

    return {pendingComments: comments, threadsWithoutContext: threadsWithoutContextInt}
  }, [diffEntries, threads, pendingReviewIds])

  const threadPreviewPositioningMap = useThreadPreviewPositioning(threadsWithoutContext).data

  const stitchedComments = useMemo(() => {
    if (threadsWithoutContext.length === 0 || !threadPreviewPositioningMap) {
      return pendingComments
    }

    // Create new objects instead of mutating to trigger React re-renders
    return pendingComments.map(pendingComment => {
      const threadId = pendingComment.thread.id

      const threadPreviewPositioning = threadPreviewPositioningMap.threads[threadId]
      if (!threadPreviewPositioning) {
        return pendingComment
      }

      return {
        ...pendingComment,
        thread: {
          ...pendingComment.thread,
          path: threadPreviewPositioning.path ?? pendingComment.thread.path,
          subject: positionedThreadToSubject(threadPreviewPositioning),
        },
      }
    })
  }, [pendingComments, threadsWithoutContext, threadPreviewPositioningMap])

  return stitchedComments
}
